import { EmptyState, Pill } from "@signet/ui";
import { compact, CreatorAvatar, type CreatorPhotos } from "@signet/web-kit";
import { BAN_REASON } from "../../shared/lifecycle.ts";
import { shortDate, type Stock } from "./api.ts";

/* ──────────────────────────────────────────────────────────────────────────
   Graveyard (the boss, 2026-10-21): a ban that can't get back comes off the
   phone and lands here. That is a 3 reason ban whose appeal didn't come
   back, and a connected ban, which is never appealed, unless its 3 reason
   ban is still waiting. Which ones is shared/appeal.ts offPhone; nothing is
   stored, so Came back or Undo the ban puts an account back on its phone.
   ────────────────────────────────────────────────────────────────────────── */

function why(a: Stock): string {
  if (a.banReason === "three_reasons") return "Appealed, didn't come back";
  if (a.causedBy) return `Came from @${a.causedBy.username}, which didn't come back`;
  return "Connected ban, can't be appealed";
}

export function GraveyardView({ accounts, photos, onOpen }: { accounts: Stock[]; photos: CreatorPhotos; onOpen: (id: string) => void }) {
  const dead = accounts.filter((a) => a.offPhone).sort((a, b) => (b.statusOn ?? "").localeCompare(a.statusOn ?? ""));
  return (
    <section className="igs-appeals">
      <div className="igs-appeals-head">
        <p>Bans that can't get back come off their phone and land here: a 3 reason ban that didn't come back, and connected bans with no appeal left to wait on.</p>
      </div>
      {dead.length ? (
        <ul className="igs-appeals-list">
          {dead.map((a) => (
            <li key={a.id}>
              <button type="button" className="igs-appeals-row igs-grave-row" onClick={() => onOpen(a.id)}>
                <span className="igs-appeals-who">
                  {a.creator ? <CreatorAvatar name={a.creator} src={photos(a.creator)} size={30} /> : null}
                  <span>
                    <strong>@{a.username}</strong>
                    <small>{[a.phone ? `Was on ${a.posterName ? `${a.posterName}'s phone` : "phone"} ${a.phone}` : null, a.statusOn ? `banned ${shortDate(a.statusOn)}` : null].filter(Boolean).join(", ")}</small>
                  </span>
                </span>
                <span>{a.banReason ? <Pill tone="bad" dot>{BAN_REASON[a.banReason]}</Pill> : null}</span>
                <span className="igs-appeals-note"><span>{why(a)}</span></span>
                <span className="igs-appeals-stake tabular">
                  <strong>{a.followers === null ? "-" : compact(a.followers)}</strong>
                  <small className="faint">followers at last read</small>
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState title="Nothing in the Graveyard" body="A ban shows here once it can't get back." />
      )}
    </section>
  );
}
